/* eslint-disable @typescript-eslint/no-explicit-any */
import apiRoutes from "@/src/apiRoutes";
import { textVariants } from "@/src/components/Text/Text";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import useUrlState from "@/src/hooks/use-url-state";
import api from "@/src/utils";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { TFAData } from "../../../types";
import Loader from "@/src/components/Loader";
import EnableTfaApp from "./EnableTfaApp";
import { useFirstPaint } from "@/src/hooks/useFirstPaint";
import { isEmpty } from "lodash";
import ChangeEmail from "./ChangeEmail";
import ChangePhone from "./ChangePhone";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Main = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.settings.securityCenter.${key}`);
  const [modal, setModal] = useUrlState("modal");
  const [openEmail, setOpenEmail] = useState(false);
  const [openPhone, setOpenPhone] = useState(false);

  // function to check enabled statuses
  const loadTfaEnabled = async () => {
    const res = await api.get(apiRoutes.tfa);

    return res?.data;
  };

  // Query to get enabled statuses
  const { data, isLoading } = useQuery<TFAData>({
    queryKey: ["check_tfa_enabled"],
    queryFn: loadTfaEnabled,
  });

  const { firstPaint } = useFirstPaint();

  if (isLoading || !firstPaint) return <Loader />;

  return (
    <div className="grid gap-5">
      <div className="grid gap-2">
        <span
          className={textVariants({
            size: "lg",
            variant: "header",
            font: "medium",
          })}
        >
          {trans("header")}{" "}
          <span
            className={cn(
              firstPaint?.security_level < 40
                ? "text-red-500"
                : firstPaint?.security_level < 80
                ? "text-yellow-500"
                : "text-green-500"
            )}
          >
            {firstPaint?.security_level}%
          </span>
        </span>
        <div className="flex">
          <div
            className={cn(
              firstPaint?.security_level < 40
                ? "text-red-500"
                : firstPaint?.security_level < 70
                ? "text-yellow-700 dark:text-yellow-50"
                : "text-green-500 dark:text-green-50",
              firstPaint?.security_level < 40
                ? "bg-red-50"
                : firstPaint?.security_level < 70
                ? "bg-yellow-200/60 dark:bg-yellow-600/30"
                : "bg-green-50 dark:bg-green-800",
              "py-2 px-4 rounded-lg text-sm relative  "
            )}
          >
            {firstPaint?.security_level < 40
              ? trans("lowDesc")
              : firstPaint?.security_level < 70
              ? trans("mediumDesc")
              : trans("highDesc")}
          </div>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{trans("verificationMethods.header")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <ul className=" divide-y divide-gray-200 dark:divide-grey-600">
              {data?.info.enabled ? (
                <Link
                  to={"tfa-enabled"}
                  className="py-4 flex items-center justify-between cursor-pointer"
                >
                  <div className="flex flex-col">
                    <p className={textVariants({ size: "lg" })}>
                      {trans("verificationMethods.authApp.header")}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("verificationMethods.authApp.enabled")}
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  </div>
                </Link>
              ) : (
                <li
                  onClick={() => setModal("auth_app")}
                  className="py-4 flex items-center justify-between cursor-pointer"
                >
                  <div className="flex flex-col">
                    <p className={textVariants({ size: "lg" })}>
                      {trans("verificationMethods.authApp.header")}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("verificationMethods.authApp.disabled")}
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  </div>
                </li>
              )}
              <li
                // onClick={() => setModal("change-email")}
                onClick={() => setOpenEmail(true)}
                className="py-4 flex items-center justify-between cursor-pointer"
              >
                <div className="flex flex-col">
                  <p className={textVariants({ size: "lg" })}>
                    {trans("verificationMethods.email.header")}
                  </p>
                  <span className={textVariants({ size: "xs" })}>
                    {firstPaint?.profile.email}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className={textVariants({ variant: "desc" })}>
                    {!isEmpty(firstPaint?.profile.email)
                      ? trans("verificationMethods.email.linked")
                      : trans("verificationMethods.email.notLinked")}
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                </div>
              </li>
              <li
                // onClick={() => setModal("change-phone")}
                onClick={() => setOpenPhone(true)}
                className="py-4 flex items-center justify-between cursor-pointer"
              >
                <div className="flex flex-col">
                  <p className={textVariants({ size: "lg" })}>
                    {trans("verificationMethods.phone.header")}
                  </p>
                  <span className={textVariants({ size: "xs" })}>
                    {firstPaint?.profile.phone}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className={textVariants({ variant: "desc" })}>
                    {!isEmpty(firstPaint?.profile.phone)
                      ? trans("verificationMethods.phone.linked")
                      : trans("verificationMethods.phone.notLinked")}
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                </div>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <ul className=" divide-y divide-gray-200 dark:divide-grey-600">
              <Link
                to={"change-password"}
                className="py-4 flex items-center justify-between"
              >
                <div className="flex flex-col">
                  <p className={textVariants({ size: "lg" })}>
                    {trans("account.changePassword.title")}
                  </p>
                </div>
                <div className="flex items-center">
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                </div>
              </Link>
              <Link
                to={"delete-account"}
                className="py-4 flex items-center justify-between"
              >
                <div className="flex flex-col">
                  <p className={textVariants({ size: "lg" })}>
                    {trans("account.deleteAccount.title")}
                  </p>
                </div>
                <div className="flex items-center">
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                </div>
              </Link>
            </ul>
          </div>
        </CardContent>
      </Card>
      <EnableTfaApp
        open={modal === "auth_app"}
        setOpen={(val: any) => setModal(val ? undefined : "auth_app")}
        data={data}
      />
      <ChangeEmail
        open={openEmail}
        setOpen={setOpenEmail}
        data={firstPaint?.profile}
      />
      <ChangePhone
        open={openPhone}
        setOpen={setOpenPhone}
        data={firstPaint?.profile}
      />
    </div>
  );
};

export default Main;
